import React from 'react'
import { Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { BLOCKS } from '@contentful/rich-text-types'
import CustomComponent from '../CustomComponent'
import NavigationGroupLink from '../NavigationGroup/NavigationGroupLink'
import RichText from '../RichText'
import Testimonial from '../Testimonials/Testimonial'
import { PackageSection as PackageSectionType } from '../../types/packages'
import { CustomComponentTypes } from '../../types/landingPageV2'
import useHide from '../../hooks/useHide'

export interface PackageSectionProps {
  packageSection: PackageSectionType
  inCarousel?: boolean
}

const PackageSection: React.FC<PackageSectionProps> = ({
  packageSection,
  inCarousel = false,
}) => {
  const { classes, cx } = useStyles()
  const hideClasses = useHide(packageSection.hideOn)

  if (packageSection) {
    return (
      <Box
        className={cx(
          {
            [classes.box]: true,
            [classes.boxExpanded]:
              packageSection?.packageCard?.name ===
              CustomComponentTypes.PackageExecutiveExpanded,
          },
          hideClasses
        )}
      >
        <Box
          className={cx({
            [classes.background]:
              packageSection?.packageCard?.name !==
              CustomComponentTypes.PackageExecutiveExpanded,
            [classes.backgroundExpanded]:
              packageSection?.packageCard?.name ===
              CustomComponentTypes.PackageExecutiveExpanded,
          })}
        />
        <Box
          className={cx({
            [classes.textBox]: true,
            [classes.textBoxExpanded]:
              packageSection?.packageCard?.name ===
              CustomComponentTypes.PackageExecutiveExpanded,
          })}
        >
          {packageSection.testimonial && (
            <Testimonial
              quoteClass={cx({
                [classes.quote]: true,
                [classes.quoteInCarousel]: inCarousel,
              })}
              testimonial={packageSection.testimonial}
            />
          )}
          {packageSection.text && (
            <RichText
              rawBody={packageSection.text.raw}
              options={{
                props: {
                  [BLOCKS.HEADING_3]: {
                    className: classes.richTextH3,
                  },
                  [BLOCKS.HR]: {
                    className: classes.richTextHR,
                  },
                },
              }}
            />
          )}
          {packageSection.button && (
            <NavigationGroupLink
              className={classes.button}
              link={packageSection.button}
            />
          )}
        </Box>
        {packageSection.packageCard && (
          <Box
            className={cx({
              [classes.packageCard]: true,
              [classes.packageCardExpanded]:
                packageSection.packageCard.name ===
                CustomComponentTypes.PackageExecutiveExpanded,
            })}
          >
            <CustomComponent
              color={packageSection.packageCardColor}
              item={packageSection.packageCard}
            />
          </Box>
        )}
      </Box>
    )
  } else {
    return null
  }
}

const useStyles = makeStyles()((theme) => ({
  background: {
    display: 'none',
  },
  backgroundExpanded: {
    backgroundColor: theme.palette.background.paper,
    height: '100%',
    position: 'absolute',
    width: '78.9%',
    zIndex: -1,

    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    '@media (min-width: 1456px)': {
      width: '70%',
    },
  },
  box: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',

    [theme.breakpoints.down('md')]: {
      flexDirection: 'column-reverse',
    },
  },
  boxExpanded: {
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  button: {
    marginTop: '2em',
  },
  packageCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginRight: 'calc(6% + 6.69em)',

    [theme.breakpoints.down('lg')]: {
      marginRight: 'calc(6% + 2em)',
    },
    [theme.breakpoints.down('md')]: {
      marginRight: '0',
      width: 'calc(100% - 12.3%)',
    },
    '@media (min-width: 1456px)': {
      marginRight: 'calc((100% - 91rem)/2 + 5.5rem + 6.69em)',
    },
  },
  packageCardExpanded: {
    maxWidth: '35%',

    [theme.breakpoints.down('lg')]: {
      maxWidth: '45%',
    },

    [theme.breakpoints.down('md')]: {
      marginBottom: '4em',
      maxWidth: 'unset',
    },
  },
  quote: {
    marginBottom: '1rem',
    maxWidth: '150%',
    width: '38.44em',

    [theme.breakpoints.down('lg')]: {
      maxWidth: '75%',
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: 'unset',
      width: 'unset',

      '& h2': {
        fontSize: '1.81em',
      },
    },
  },
  quoteInCarousel: {
    minHeight: '14.5em',

    [theme.breakpoints.down('lg')]: {
      minHeight: '17em',
    },
    [theme.breakpoints.down('md')]: {
      minHeight: '15.5em',
    },
  },
  richTextH3: {
    color: theme.palette.primary.main,
  },
  richTextHR: {
    margin: '1.5em 0',
    width: '100%',
  },
  textBox: {
    alignItems: 'flex-start',
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '40%',
    padding: '4em 0 7.63em calc(6% + 6.93em)',
    width: '37.3em',

    [theme.breakpoints.down('lg')]: {
      padding: '4em 0 7.63em calc(6% + 2em)',
    },
    [theme.breakpoints.down('md')]: {
      alignItems: 'center',
      marginTop: '-13.5em',
      maxWidth: 'unset',
      padding: '16.13em 6% 6em 6%',
      width: '100%',
    },
    '@media (min-width: 1456px)': {
      maxWidth: '100%',
      padding: '4em 0 7.63em calc((100% - 91rem)/2 + 5.5rem + 2em)',
      width: 'calc((100% - 91rem)/2 + 37.3em)',
    },
  },
  textBoxExpanded: {
    backgroundColor: 'unset',
    margin: '4em 0 7.63em calc(6% + 6.93em)',
    padding: '0',
    maxWidth: '45%',
    width: '25em',

    [theme.breakpoints.down('lg')]: {
      margin: '4em 0 4.68em 6%',
      maxWidth: '37%',
    },
    [theme.breakpoints.down('md')]: {
      margin: '0',
      maxWidth: 'unset',
      padding: '4em 6% 4.68em 6%',
      textAlign: 'center',
      width: '100%',
    },
    '@media (min-width: 1456px)': {
      margin: '4em 0 4.68em calc((100% - 91rem)/2 + 5.5rem)',
    },
  },
}))

export default PackageSection
